import { useEffect } from "react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { offlineQueue } from "@/lib/offlineQueue";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

export function OfflineSync() {
  const { isOnline } = useOnlineStatus();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isOnline) return;

    const syncQueue = async () => {
      const queue = await offlineQueue.getAll();
      if (queue.length === 0) return;

      toast({
        title: "Syncing offline attempts...",
        description: `Uploading ${queue.length} attempts`,
      });

      let synced = 0;
      let failed = 0;

      for (const attempt of queue) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error("Not authenticated");

          const { error } = await supabase.from("attempts").insert({
            user_id: user.id,
            mcq_id: attempt.mcq_id,
            is_correct: attempt.is_correct,
            time_taken_seconds: attempt.time_taken_seconds,
            attempted_at: attempt.attempted_at,
          });

          if (error) throw error;

          await offlineQueue.remove(attempt.id);
          synced++;
        } catch (error) {
          console.error("Failed to sync attempt:", error);
          failed++;
        }
      }

      if (synced > 0) {
        queryClient.invalidateQueries({ queryKey: ["attempts"] });
        queryClient.invalidateQueries({ queryKey: ["offline-queue-count"] });
        
        toast({
          title: "Sync complete",
          description: `${synced} attempts synced${failed > 0 ? `, ${failed} failed` : ""}`,
        });
      }
    };

    syncQueue();
  }, [isOnline, toast, queryClient]);

  return null;
}
